/**
 * Replaces the 95 KB jQuery Inputmask bundle. Every mask the app uses is a fixed
 * numeric pattern, so laying digits into a literal template covers all of them.
 *
 *   9 = one digit, anything else = a literal kept as typed
 */
export const MASKS: Record<string, string> = {
  "mask-phone": "+7 (999) 999-99-99",
  "mask-date": "99.99.9999",
  "mask-date-time": "99.99.9999, 99:99",
  "mask-duration": "99:99 — 99:99",
  "mask-time": "99:99",
  "mask-code": "999-999",
  "mask-snils": "999-999-999-99",
};

/**
 * «Сейчас» для значка часов у поля с маской: дата, дата и время (если поле
 * с временем и значок не «date-only») или время. null — поле не дата.
 * now — уже в нужном поясе (helpers.ts: nowMsk).
 */
export function nowForMask(pattern: string | null, now: Date, dateOnly = false): string | null {
  const p2 = (n: number) => String(n).padStart(2, "0");
  const date = `${p2(now.getDate())}.${p2(now.getMonth() + 1)}.${now.getFullYear()}`;
  const time = `${p2(now.getHours())}:${p2(now.getMinutes())}`;
  if (pattern === MASKS["mask-date"]) return date;
  if (pattern === MASKS["mask-date-time"]) return dateOnly ? date : `${date}, ${time}`;
  if (pattern === MASKS["mask-time"]) return time;
  return null;
}

/** Returns the mask pattern implied by a className, if any. */
export function maskFor(className: string | undefined): string | null {
  if (!className) return null;
  for (const cls of className.split(/\s+/)) {
    if (cls === "mask-numeric") return "numeric";
    if (MASKS[cls]) return MASKS[cls];
  }
  return null;
}

export function applyMask(pattern: string, raw: string): string {
  if (!raw) return "";
  if (pattern === "numeric") return raw.replace(/\D/g, "");

  // A phone mask carries its own leading "+7"; a value the user pasted with the
  // country code must not shift every digit one place to the right.
  let digits = raw.replace(/\D/g, "");
  if (pattern.startsWith("+7")) {
    if (digits.startsWith("8")) digits = "7" + digits.slice(1);
    if (digits.startsWith("7")) digits = digits.slice(1);
  }

  let out = "";
  let di = 0;
  for (const ch of pattern) {
    if (ch === "9") {
      if (di >= digits.length) break;
      out += digits[di++];
    } else if (pattern.startsWith("+7") && (ch === "+" || ch === "7")) {
      out += ch;
    } else {
      if (di >= digits.length) break;
      out += ch;
    }
  }
  return out;
}

/* ------------------------------------------------- правка внутри значения */

/** Пустая цифра внутри значения: «1_.05.2026» — день стёрли, остальное на месте. */
const HOLE = "_";

/** Позиции цифр («9») в шаблоне. */
function slotPositions(pattern: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < pattern.length; i++) if (pattern[i] === "9") out.push(i);
  return out;
}

/**
 * Значение, набранное по шаблону, → цифра (или null — пусто) на каждом месте.
 * null, если значение не по шаблону (старые данные, вставка из другого места).
 */
function readSlots(pattern: string, value: string): (string | null)[] | null {
  if (value.length > pattern.length) return null;
  const slots: (string | null)[] = [];
  for (let i = 0; i < value.length; i++) {
    const p = pattern[i];
    const c = value[i];
    if (p === "9") {
      if (/\d/.test(c)) slots.push(c);
      else if (c === HOLE) slots.push(null);
      else return null;
    } else if (c !== p) return null;
  }
  const total = slotPositions(pattern).length;
  while (slots.length < total) slots.push(null);
  return slots;
}

/** Цифры по местам → строка; хвост из пустых мест не пишется. */
function writeSlots(pattern: string, slots: (string | null)[]): string {
  let last = -1;
  slots.forEach((d, i) => {
    if (d !== null) last = i;
  });
  if (last < 0) return "";
  let out = "";
  let si = 0;
  for (const p of pattern) {
    if (si > last) break;
    if (p === "9") out += slots[si++] ?? HOLE;
    else out += p;
  }
  return out;
}

/**
 * Правка в поле с маской — как в Inputmask старого сайта: цифры стоят на своих
 * местах. Набранная цифра заменяет ту, что под кареткой (а не сдвигает все
 * следующие), Backspace/Delete стирают одну цифру, оставляя «_», точки и
 * двоеточия не двигаются. Так поменять день или месяц в готовой дате — это
 * поставить каретку и набрать, а не стирать всё и набирать заново.
 *
 * prev — значение до правки, next — что стало в поле, caret — каретка после
 * правки, inputType — InputEvent.inputType (Backspace или Delete на точке).
 */
export function maskEdit(
  pattern: string,
  prev: string,
  next: string,
  caret: number,
  inputType = ""
): { value: string; caret: number } {
  if (pattern === "numeric") {
    const value = applyMask(pattern, next);
    return { value, caret: Math.min(caret, value.length) };
  }
  const slots = readSlots(pattern, prev);
  // Значение не по шаблону — разложить цифры заново, как раньше.
  if (!slots) {
    const value = applyMask(pattern, next);
    return { value, caret: value.length };
  }
  const pos = slotPositions(pattern);

  // Что изменилось: общий хвост (не дальше каретки — правка кончается на ней),
  // затем общее начало. Так «11.05» → «111.05» читается как набор на месте 1,
  // а не 2, хотя цифры одинаковые.
  let b = 0;
  const maxB = Math.min(prev.length, next.length - caret);
  while (b < maxB && prev[prev.length - 1 - b] === next[next.length - 1 - b]) b++;
  let a = 0;
  const maxA = Math.min(prev.length - b, next.length - b);
  while (a < maxA && prev[a] === next[a]) a++;
  const removedEnd = prev.length - b;
  const inserted = next.slice(a, next.length - b);

  let cleared = false;
  for (let i = a; i < removedEnd; i++) {
    const s = pos.indexOf(i);
    if (s >= 0) {
      slots[s] = null;
      cleared = true;
    }
  }

  let digits = inserted.replace(/\D/g, "");
  let at = a;
  if (digits) {
    let s = pos.findIndex((p) => p >= a);
    if (s < 0) s = pos.length;
    // Телефон вставили с кодом страны: «8 912…», «+7 912…».
    if (pattern.startsWith("+7") && digits.length > pos.length - s && /^[78]/.test(digits)) digits = digits.slice(1);
    for (const d of digits) {
      if (s >= pos.length) break;
      slots[s] = d;
      at = pos[s] + 1;
      s++;
    }
  } else if (!cleared && removedEnd > a) {
    // Стёрли только точку или двоеточие — стереть цифру рядом с ним.
    if (inputType === "deleteContentForward") {
      const s = pos.findIndex((p) => p >= a);
      if (s >= 0) slots[s] = null;
    } else {
      let s = -1;
      for (let i = 0; i < pos.length; i++) if (pos[i] < a) s = i;
      if (s >= 0) {
        slots[s] = null;
        at = pos[s];
      }
    }
  } else if (!cleared && inserted && pattern[a] === inserted[0]) {
    // Набрали точку там, где она и стоит, — каретка просто проходит её.
    at = a + 1;
  }

  const value = writeSlots(pattern, slots);
  return { value, caret: Math.min(at, value.length) };
}

/**
 * onChange поля с маской: новое значение (его и сохранять) и каретка на
 * месте правки. Значение и каретка ставятся в поле сразу, здесь же: React,
 * перерисовав поле, видит в нём уже нужное значение и не трогает его — иначе
 * каретка прыгала бы в конец, и следующая цифра уходила бы мимо.
 */
export function maskChange(
  e: { target: HTMLInputElement; nativeEvent: Event },
  pattern: string,
  prev: string
): string {
  const el = e.target;
  const caret = el.selectionStart ?? el.value.length;
  const inputType = (e.nativeEvent as InputEvent).inputType ?? "";
  const r = maskEdit(pattern, prev ?? "", el.value, caret, inputType);
  if (el.value !== r.value) el.value = r.value;
  if (document.activeElement === el) el.setSelectionRange(r.caret, r.caret);
  return r.value;
}
